import type { TableFetchResult, TableQueryParams } from "@/lib/table"

export type ConsoleUserId = string & { readonly __brand: "ConsoleUserId" }
export type ServiceKeyId = string & { readonly __brand: "ServiceKeyId" }

// Display vocabulary for this admin surface. Mirrors wire `Role` (lib/roles) except that
// the wire `sme` is shown as "specialist" — see `fromWireRole` / `toWireRole` in
// users-keys-api.ts for the boundary translation. Named constants everywhere so a rename
// is a single edit.
export const ConsoleRole = {
  ADMIN: "admin",
  REVIEWER: "reviewer",
  SPECIALIST: "specialist",
  VIEWER: "viewer",
  CLIENT: "client",
} as const
export type ConsoleRole = (typeof ConsoleRole)[keyof typeof ConsoleRole]

export interface ConsoleUser {
  id: ConsoleUserId
  email: string
  name: string | null
  role: ConsoleRole
  isBootstrap: boolean
  createdAt: string
  lastSeen: string | null
  invitedAt: string | null
  acceptedAt: string | null
  isActive: boolean
}

export type AccountStatus = "active" | "pending-invite" | "never-signed-in" | "deactivated"

export function accountStatus(user: ConsoleUser): AccountStatus {
  // Pending invite wins over `!isActive` — an invitee is inactive by construction until acceptance.
  if (user.invitedAt !== null && user.acceptedAt === null) return "pending-invite"
  if (!user.isActive) return "deactivated"
  if (user.lastSeen === null && !user.isBootstrap) return "never-signed-in"
  return "active"
}

export interface ServiceKey {
  id: ServiceKeyId
  name: string
  prefix: string
  createdAt: string
  createdBy: string
  lastUsedAt: string | null
  fromEnv: boolean
}

export interface RoleOption {
  value: ConsoleRole
  label: string
  description: string
}

export const ROLE_OPTIONS: readonly RoleOption[] = [
  {
    value: ConsoleRole.ADMIN,
    label: "Admin",
    description: "Everything, plus users and keys.",
  },
  {
    value: ConsoleRole.REVIEWER,
    label: "Reviewer",
    description: "Runs reviews and dispositions findings.",
  },
  {
    value: ConsoleRole.SPECIALIST,
    label: "Specialist",
    description: "Answers findings the reviewer sent to a subject-matter expert.",
  },
  {
    value: ConsoleRole.VIEWER,
    label: "Viewer",
    description: "Reads reviews and reports. Cannot change anything.",
  },
  {
    value: ConsoleRole.CLIENT,
    label: "Client contact",
    description: "Sees one engagement's reviews and reports, nothing else.",
  },
] as const

export function labelOfRole(role: ConsoleRole): string {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role
}

export interface UserFilters {
  role?: ConsoleRole
  activity?: "signed-in" | "never"
}

const USER_SORTERS: Record<string, (a: ConsoleUser, b: ConsoleUser) => number> = {
  lastSeen: (a, b) => (b.lastSeen ?? "").localeCompare(a.lastSeen ?? ""),
  email: (a, b) => a.email.localeCompare(b.email),
  role: (a, b) => a.role.localeCompare(b.role),
  createdAt: (a, b) => b.createdAt.localeCompare(a.createdAt),
}

function userMatches(u: ConsoleUser, filters: UserFilters, needle: string): boolean {
  if (filters.role && u.role !== filters.role) return false
  if (filters.activity === "signed-in" && u.lastSeen === null) return false
  if (filters.activity === "never" && u.lastSeen !== null) return false
  if (!needle) return true
  const hay = `${u.email} ${u.name ?? ""} ${labelOfRole(u.role)}`.toLowerCase()
  return hay.includes(needle)
}

export function filterUsers(rows: readonly ConsoleUser[], filters: UserFilters): ConsoleUser[] {
  return rows.filter((u) => userMatches(u, filters, ""))
}

export function queryUsers(
  rows: ConsoleUser[],
  params: TableQueryParams & { filters: UserFilters },
): TableFetchResult<ConsoleUser> {
  const needle = params.search.trim().toLowerCase()
  const filtered = rows.filter((u) => userMatches(u, params.filters, needle))

  if (params.sort) {
    const cmp = USER_SORTERS[params.sort.field]
    if (cmp) {
      filtered.sort((a, b) => (params.sort!.dir === "asc" ? cmp(a, b) : -cmp(a, b)))
    }
  }

  const start = (params.page - 1) * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

export interface KeyFilters {
  origin?: "user" | "env"
  activity?: "used" | "never"
}

const KEY_SORTERS: Record<string, (a: ServiceKey, b: ServiceKey) => number> = {
  createdAt: (a, b) => b.createdAt.localeCompare(a.createdAt),
  lastUsedAt: (a, b) => (b.lastUsedAt ?? "").localeCompare(a.lastUsedAt ?? ""),
  name: (a, b) => a.name.localeCompare(b.name),
}

function keyMatches(k: ServiceKey, filters: KeyFilters, needle: string): boolean {
  if (filters.origin === "user" && k.fromEnv) return false
  if (filters.origin === "env" && !k.fromEnv) return false
  if (filters.activity === "used" && k.lastUsedAt === null) return false
  if (filters.activity === "never" && k.lastUsedAt !== null) return false
  if (!needle) return true
  const hay = `${k.name} ${k.prefix} ${k.createdBy}`.toLowerCase()
  return hay.includes(needle)
}

export function filterKeys(rows: readonly ServiceKey[], filters: KeyFilters): ServiceKey[] {
  return rows.filter((k) => keyMatches(k, filters, ""))
}

export function queryKeys(
  rows: ServiceKey[],
  params: TableQueryParams & { filters: KeyFilters },
): TableFetchResult<ServiceKey> {
  const needle = params.search.trim().toLowerCase()
  const filtered = rows.filter((k) => keyMatches(k, params.filters, needle))

  if (params.sort) {
    const cmp = KEY_SORTERS[params.sort.field]
    if (cmp) {
      filtered.sort((a, b) => (params.sort!.dir === "asc" ? cmp(a, b) : -cmp(a, b)))
    }
  }

  const start = (params.page - 1) * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}
